import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, 
  Send, 
  Loader2, 
  Brain, 
  TrendingUp, 
  Users, 
  User,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Copy,
  Eye,
  Target,
  ThumbsUp,
  ThumbsDown,
  Star,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface AIQueryResult {
  success: boolean;
  data?: any[];
  sql_query?: string;
  explanation?: string;
  error?: string;
  response_time_ms?: number;
  learning_context?: {
    confidence_score: number;
    similar_queries_found: number;
    patterns_applied: string[];
  };
  pattern_id?: number;
  quota_status?: {
    can_query: boolean;
    queries_used: number;
    queries_limit: number;
    queries_remaining: number;
    subscription_status: string;
    usage_percentage: number;
  };
  message?: string;
}

interface QuerySuggestion {
  text: string;
  category: string;
  icon: React.ElementType;
  description: string;
  badge?: string;
}

const FEATURED_SUGGESTIONS: QuerySuggestion[] = [
  {
    text: "How many students do I have between the ages of 5 and 10?",
    category: "Demographics",
    icon: Users,
    description: "Get student count by age range",
    badge: "Student Analytics"
  },
  {
    text: "List my student's birthdays?",
    category: "Student Info",
    icon: Calendar,
    description: "View upcoming student birthdays",
    badge: "Quick Info"
  },
  {
    text: "List my families who have more than one student?",
    category: "Family Analytics",
    icon: Users,
    description: "Find multi-student families",
    badge: "Family Insights"
  },
  {
    text: "How many students are enrolled for each class we offer?",
    category: "Class Management",
    icon: Target,
    description: "View enrollment numbers by class",
    badge: "Class Analytics"
  },
  {
    text: "Find students without primary emergency contacts",
    category: "Safety Management", 
    icon: AlertCircle,
    description: "Identify students missing emergency contact info",
    badge: "Safety Check"
  }
];

const QUICK_QUESTIONS: QuerySuggestion[] = [
  {
    text: "How many students enrolled this week?",
    category: "Quick Stats",
    icon: Users,
    description: "Current enrollment numbers"
  },
  {
    text: "What's today's attendance rate?",
    category: "Quick Stats", 
    icon: Calendar,
    description: "Daily attendance tracking"
  },
  {
    text: "Show me this month's revenue",
    category: "Quick Stats",
    icon: DollarSign,
    description: "Monthly financial summary"
  }
];

const AskHivetivityInterface: React.FC = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<AIQueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showSQL, setShowSQL] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{query: string, result: AIQueryResult}>>([]);
  const [quotaStatus, setQuotaStatus] = useState<{
    can_query: boolean;
    queries_used: number;
    queries_limit: number;
    queries_remaining: number;
    subscription_status: string;
    usage_percentage: number;
  } | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch current quota status
  const fetchQuotaStatus = async () => {
    if (!user) {
      console.log('No user found, skipping quota fetch');
      return;
    }
    
    // Get organization_id from user profile
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', user.id)
      .single();
    
    if (profileError || !profile?.organization_id) {
      console.log('No organization_id found for user:', user.id, profileError);
      return;
    }
    
    console.log('Fetching quota status for user:', user.id, 'org:', profile.organization_id);
    setQuotaLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-usage-status', {
        body: { organization_id: profile.organization_id }
      });

      console.log('Quota API response:', { data, error });

      if (error) throw error;
      
      if (data?.success && data?.data) {
        console.log('Setting quota status:', data.data);
        setQuotaStatus(data.data);
      } else {
        console.log('No quota data received or unsuccessful response');
      }
    } catch (error) {
      console.warn('Failed to fetch quota status:', error);
    } finally {
      setQuotaLoading(false);
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
    fetchQuotaStatus();
  }, [user]);

  // Clear result and allow new question
  const handleClearResult = () => {
    setResult(null);
    setQuery('');
    inputRef.current?.focus();
    toast.success('Ready for your next question!');
  };

  const handleQuery = async (questionText?: string) => {
    const questionToAsk = questionText || query;
    if (!questionToAsk.trim()) return;

    setLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('ai-query-learning', {
        body: { query: questionToAsk }
      });

      if (error) throw error;

      const queryResult = data as AIQueryResult;
      setResult(queryResult);
      
      // Update quota status if provided in response
      if (queryResult.quota_status) {
        setQuotaStatus(queryResult.quota_status);
      }
      
      // Add to conversation history
      setConversationHistory(prev => [...prev.slice(-4), { query: questionToAsk, result: queryResult }]);

      if (queryResult.success) {
        toast.success('AI analysis complete');
        // Refresh quota status after successful query
        setTimeout(fetchQuotaStatus, 1000);
      } else {
        // Handle quota exceeded error specially
        if (queryResult.error === 'quota_exceeded') {
          toast.error(queryResult.message || 'Monthly AI query limit reached');
        } else {
          toast.error('Query failed: ' + queryResult.error);
        }
      }
    } catch (error: any) {
      const errorResult: AIQueryResult = {
        success: false,
        error: error.message || 'Failed to process your question'
      };
      setResult(errorResult);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
      if (!questionText) setQuery('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuery();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleFeedback = async (rating: number, feedback?: string) => {
    if (!result?.pattern_id) return;

    try {
      const { error } = await supabase
        .from('ai_query_patterns')
        .update({
          user_rating: rating,
          user_feedback: feedback || null,
          was_helpful: rating >= 3,
          updated_at: new Date().toISOString()
        })
        .eq('id', result.pattern_id);

      if (error) throw error;

      toast.success('Thank you for your feedback!');
      
      // Update local state
      setResult(prev => prev ? {...prev, user_rating: rating} : null);
    } catch (error) {
      console.error('Failed to record feedback:', error);
      toast.error('Failed to record feedback');
    }
  };

  const formatDataForDisplay = (data: any[]) => {
    if (!data || data.length === 0) return null;

    // Enhanced single value display
    if (data.length === 1 && Object.keys(data[0]).length === 1) {
      const [key, value] = Object.entries(data[0])[0];
      return (
        <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
          <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            {typeof value === 'number' && key.includes('revenue') 
              ? `$${(value / 100).toLocaleString()}` 
              : value?.toLocaleString() || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 capitalize font-medium">
            {key.replace(/_/g, ' ')}
          </div>
        </div>
      );
    }

    // Enhanced table display
    const headers = Object.keys(data[0]);
    return (
      <div className="overflow-hidden rounded-xl border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {headers.map(header => (
                  <th key={header} className="text-left p-4 font-semibold text-gray-900 capitalize">
                    {header.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.slice(0, 100).map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  {headers.map(header => (
                    <td key={header} className="p-4 text-gray-900">
                      {typeof row[header] === 'number' && header.includes('amount')
                        ? `$${(row[header] / 100).toFixed(2)}`
                        : row[header]?.toString() || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.length > 100 && (
          <div className="bg-gray-50 px-4 py-2 text-xs text-gray-600 border-t">
            Showing first 100 of {data.length} results
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Main AI Interface Card */}
      <Card className="border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-600 rounded-full blur-sm opacity-30 animate-pulse"></div>
                <Brain className="relative h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Ask Hivetivity AI
                </CardTitle>
                <CardDescription className="text-gray-600 text-sm">
                  Get instant insights from your business data
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {result && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearResult}
                  className="h-8 px-3 text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Ask New Question
                </Button>
              )}
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                <Sparkles className="h-3 w-3 mr-1" />
                NEW
              </Badge>
            </div>
          </div>
        </CardHeader>

        {/* AI Usage Quota Display */}
        {console.log('Rendering quota display, quotaStatus:', quotaStatus)}
        {quotaStatus && (
          <div className="px-6 pb-2">
            <div className="bg-white/60 border border-blue-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">AI Queries This Month</span>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    quotaStatus.subscription_status === 'trial_active' 
                      ? 'border-orange-200 text-orange-700 bg-orange-50' 
                      : 'border-blue-200 text-blue-700 bg-blue-50'
                  }`}
                >
                  {quotaStatus.subscription_status === 'trial_active' ? 'Trial' : 'Pro'}
                </Badge>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">
                    {quotaStatus.queries_used} / {quotaStatus.queries_limit} used
                  </span>
                  <span className="text-gray-500">
                    {quotaStatus.queries_remaining} remaining
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      quotaStatus.usage_percentage >= 90 
                        ? 'bg-red-500' 
                        : quotaStatus.usage_percentage >= 75 
                        ? 'bg-yellow-500' 
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(quotaStatus.usage_percentage, 100)}%` }}
                  />
                </div>
                
                {quotaStatus.usage_percentage >= 75 && (
                  <div className={`flex items-center text-xs ${
                    quotaStatus.usage_percentage >= 90 ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {quotaStatus.usage_percentage >= 90 
                      ? 'Almost at limit - consider upgrading'
                      : 'Approaching monthly limit'
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <CardContent className="space-y-4">
          {/* Main Query Input */}
          <div className="relative">
            <div className="flex space-x-3">
              <div className="relative flex-1">
                <Input
                  ref={inputRef}
                  placeholder="Ask anything... 'How many students enrolled this week?'"
                  aria-label="Ask Hivetivity AI a question"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  className="pl-4 pr-10 py-2 text-sm border border-blue-200 focus:border-blue-400 rounded-lg bg-white/80 backdrop-blur-sm"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Brain className="h-4 w-4 text-blue-400" />
                </div>
              </div>
              <Button 
                onClick={() => handleQuery()} 
                disabled={loading || !query.trim()}
                size="sm"
                className="px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg shadow-md transition-all duration-200"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1" />
                    Ask
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Suggestions */}
          {!result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Target className="h-4 w-4 text-blue-600" />
                  <h4 className="font-medium text-gray-900 text-sm">Try asking:</h4>
                </div>
                <Badge variant="outline" className="text-xs">Popular queries</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {FEATURED_SUGGESTIONS.map((suggestion, index) => {
                  const Icon = suggestion.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => handleQuery(suggestion.text)}
                      disabled={loading}
                      className="inline-flex items-center space-x-2 px-3 py-2 text-sm border border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 bg-blue-50/50 min-h-[44px]"
                    >
                      <Icon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700 text-left leading-tight max-w-[400px]">{suggestion.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Section */}
      {result && (
        <Card className="border border-gray-200 shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {result.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
                <CardTitle className="text-lg">
                  {result.success ? 'AI Analysis Results' : 'Query Error'}
                </CardTitle>
                {result.response_time_ms && (
                  <Badge variant="outline" className="text-xs">
                    {(result.response_time_ms / 1000).toFixed(1)}s
                  </Badge>
                )}
                {result.learning_context && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                    AI Confidence: {Math.round(result.learning_context.confidence_score * 100)}%
                  </Badge>
                )}
              </div>
              {result.sql_query && (
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSQL(!showSQL)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {showSQL ? 'Hide' : 'Show'} SQL
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(result.sql_query!)}
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {result.success ? (
              <>
                {/* Data Display */}
                {result.data && formatDataForDisplay(result.data)}
                
                {/* AI Explanation */}
                {result.explanation && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-start space-x-3">
                      <Brain className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">AI Business Insights</h4>
                        <div className="text-gray-700 whitespace-pre-wrap leading-relaxed mb-4">
                          {result.explanation}
                        </div>
                        
                        {/* Learning Context Display */}
                        {result.learning_context && (
                          <div className="mt-4 p-3 bg-white/60 rounded-lg border border-blue-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <Target className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-900">Learning Context</span>
                            </div>
                            <div className="text-xs text-blue-700 space-y-1">
                              {result.learning_context.similar_queries_found > 0 && (
                                <div>✓ Found {result.learning_context.similar_queries_found} similar successful queries</div>
                              )}
                              {result.learning_context.patterns_applied.length > 0 && (
                                <div>✓ Applied patterns: {result.learning_context.patterns_applied.join(', ')}</div>
                              )}
                              <div>✓ Confidence: {Math.round(result.learning_context.confidence_score * 100)}%</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Feedback Section */}
                {result.success && result.pattern_id && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Star className="h-4 w-4 text-amber-500" />
                        <span className="text-sm font-medium text-gray-900">Was this helpful?</span>
                      </div>
                      <div className="flex space-x-2">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <Button
                            key={rating}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleFeedback(rating)}
                            className="p-1 h-8 w-8"
                          >
                            <Star 
                              className={`h-4 w-4 ${
                                rating <= (result as any).user_rating 
                                  ? 'text-amber-500 fill-amber-500' 
                                  : 'text-gray-300'
                              }`} 
                            />
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-center space-x-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearResult}
                    className="border-blue-200 text-blue-600 hover:bg-blue-50"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Ask Another Question
                  </Button>
                </div>

                {/* SQL Display */}
                {showSQL && result.sql_query && (
                  <div className="bg-gray-900 rounded-xl p-4 border border-gray-300">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-mono text-sm text-gray-300">Generated SQL Query</h4>
                    </div>
                    <pre className="text-sm text-gray-100 overflow-x-auto">
                      <code>{result.sql_query}</code>
                    </pre>
                  </div>
                )}
              </>
            ) : result.error === 'quota_exceeded' ? (
              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-orange-900 mb-2">Monthly AI Limit Reached</h4>
                    <p className="text-orange-800 mb-4">
                      {result.message || 'You\'ve used all your AI queries for this month.'}
                    </p>
                    
                    {result.quota_status && (
                      <div className="bg-white/60 rounded-lg p-3 mb-4">
                        <div className="text-sm text-gray-700">
                          <strong>Current Usage:</strong> {result.quota_status.queries_used} / {result.quota_status.queries_limit} queries
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Plan: {result.quota_status.subscription_status === 'trial_active' ? 'Trial' : 'Paid'}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex space-x-3">
                      <Button 
                        size="sm" 
                        onClick={() => window.open('/subscription-center', '_blank')}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        Upgrade Plan
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={fetchQuotaStatus}
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                      >
                        Refresh Status
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">Query Failed</h4>
                    <p className="text-red-700">{result.error}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AskHivetivityInterface;
//comment